// src/pages/customer-panel/reminders/RemindersPage.js
// Reminder Management Page - Full Lifecycle

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Snackbar,
    InputAdornment,
    Tooltip,
    Tabs,
    Tab,
    List,
    ListItemButton,
    ListItemText,
    ListItem,
    ClickAwayListener,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AlarmIcon from '@mui/icons-material/Alarm';
import SnoozeIcon from '@mui/icons-material/Snooze';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const PRIORITY_CONFIG = {
    high: { color: '#ef4444', bg: '#fee2e2', label: 'High' },
    medium: { color: '#f59e0b', bg: '#fef3c7', label: 'Medium' },
    low: { color: '#3b82f6', bg: '#dbeafe', label: 'Low' },
};

const STATUS_CONFIG = {
    active: { color: '#10b981', bg: '#d1fae5', label: 'Active' },
    pending: { color: '#f59e0b', bg: '#fef3c7', label: 'Pending' },
    snoozed: { color: '#8b5cf6', bg: '#ede9fe', label: 'Snoozed' },
    dismissed: { color: '#6b7280', bg: '#f3f4f6', label: 'Dismissed' },
    completed: { color: '#10b981', bg: '#d1fae5', label: 'Completed' },
    overdue: { color: '#ef4444', bg: '#fee2e2', label: 'Overdue' },
};

const STATUS_TABS = ['all', 'active', 'snoozed', 'dismissed'];

const INITIAL_FORM = {
    title: '',
    message: '',
    priority: 'medium',
    remind_at: '',
};

// --- Debounce Hook ---
function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

// --- User Search Input ---
function UserSearchInput({ selectedUser, onSelectUser, onClearUser, placeholder }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const debouncedSearch = useDebounce(searchTerm, 300);

    useEffect(() => {
        if (debouncedSearch && debouncedSearch.length >= 2) {
            setIsSearching(true);
            axiosInstance.get('/api/users/team-members')
                .then((res) => {
                    let users = (res.data?.data || []).map((u) => ({
                        ...u,
                        fname: u.name?.split(' ')[0] || '',
                        lname: u.name?.split(' ').slice(1).join(' ') || '',
                    }));
                    // Client-side search filter
                    const term = debouncedSearch.toLowerCase();
                    users = users.filter((u) => {
                        const fullName = (u.name || '').toLowerCase();
                        const email = (u.email || '').toLowerCase();
                        return fullName.includes(term) || email.includes(term);
                    });
                    setResults(users);
                    setDropdownOpen(true);
                })
                .catch(() => setResults([]))
                .finally(() => setIsSearching(false));
        } else {
            setResults([]);
            setDropdownOpen(false);
        }
    }, [debouncedSearch]);

    const handleSelect = (user) => {
        onSelectUser(user);
        setSearchTerm('');
        setDropdownOpen(false);
    };

    if (selectedUser) {
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc',
            }}>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500 }}>
                    {selectedUser.fname || selectedUser.first_name} {selectedUser.lname || selectedUser.last_name}
                    <Typography component="span" sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', ml: 0.5 }}>
                        ({selectedUser.role || 'user'})
                    </Typography>
                </Typography>
                <IconButton size="small" onClick={onClearUser} sx={{ color: '#ef4444' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    }

    return (
        <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
            <Box sx={{ position: 'relative' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={placeholder || 'Search user...'}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setDropdownOpen(true); }}
                    sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                    InputProps={{
                        endAdornment: isSearching ? (
                            <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                        ) : null,
                    }}
                />
                {dropdownOpen && (searchTerm.length > 0 || results.length > 0) && (
                    <Paper sx={{
                        position: 'absolute', zIndex: 20, width: '100%', mt: 0.5,
                        maxHeight: 200, overflow: 'auto', borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                        <List dense disablePadding>
                            {results.length > 0 ? results.map((user) => (
                                <ListItemButton key={user.id || user.user_id} onClick={() => handleSelect(user)}>
                                    <ListItemText
                                        primary={`${user.fname || user.first_name || ''} ${user.lname || user.last_name || ''}`}
                                        secondary={user.role || ''}
                                        primaryTypographyProps={{ fontFamily, fontWeight: 500, fontSize: '0.85rem' }}
                                        secondaryTypographyProps={{ fontFamily, fontSize: '0.75rem', textTransform: 'capitalize' }}
                                    />
                                </ListItemButton>
                            )) : (
                                !isSearching && searchTerm.length >= 2 && (
                                    <ListItem>
                                        <ListItemText primary="No users found." primaryTypographyProps={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }} />
                                    </ListItem>
                                )
                            )}
                        </List>
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    );
}

// --- Format helpers ---
function formatDate(dateStr) {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return '--'; }
}

function formatDateTime(dateStr) {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch { return '--'; }
}

// --- View Reminder Dialog ---
function ViewReminderDialog({ reminder, open, onClose, currentUserId }) {
    if (!reminder) return null;
    const isForSelf = reminder.user_id === currentUserId;
    const isFromSelf = reminder.created_by_id === currentUserId;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>View Reminder</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Title</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>{reminder.title}</Typography>
                    </Box>
                    {reminder.message && (
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Message</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{reminder.message}</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>For</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                {isForSelf ? 'Myself' : `${reminder.user_name || '--'}`}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>From</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                {isFromSelf ? 'Myself' : `${reminder.creator_name || '--'}`}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Priority</Typography>
                            <Chip
                                label={(reminder.priority || 'medium').charAt(0).toUpperCase() + (reminder.priority || 'medium').slice(1).toLowerCase()}
                                size="small"
                                sx={{
                                    fontFamily, fontWeight: 600, fontSize: '0.75rem',
                                    bgcolor: (PRIORITY_CONFIG[(reminder.priority || 'medium').toLowerCase()] || PRIORITY_CONFIG.medium).bg,
                                    color: (PRIORITY_CONFIG[(reminder.priority || 'medium').toLowerCase()] || PRIORITY_CONFIG.medium).color,
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Status</Typography>
                            <Chip
                                label={(reminder.status || 'active').charAt(0).toUpperCase() + (reminder.status || 'active').slice(1).toLowerCase()}
                                size="small"
                                sx={{
                                    fontFamily, fontWeight: 600, fontSize: '0.75rem',
                                    bgcolor: (STATUS_CONFIG[(reminder.status || 'active').toLowerCase()] || STATUS_CONFIG.active).bg,
                                    color: (STATUS_CONFIG[(reminder.status || 'active').toLowerCase()] || STATUS_CONFIG.active).color,
                                }}
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Remind At</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDateTime(reminder.remind_at)}</Typography>
                    </Box>
                    {reminder.snoozed_until && (
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Snoozed Until</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#8b5cf6' }}>{formatDateTime(reminder.snoozed_until)}</Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} variant="contained" sx={{
                    fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                    textTransform: 'none', '&:hover': { bgcolor: '#059669' },
                }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Main Page ---
export default function RemindersPage() {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewFilter, setViewFilter] = useState('mine');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Modal states
    const [viewReminder, setViewReminder] = useState(null);
    const [selectedFilterUser, setSelectedFilterUser] = useState(null);
    const [createRecipient, setCreateRecipient] = useState(null);

    // User info
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const currentUserId = user?.user_id || user?.id;

    const showSnack = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchReminders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                search,
                page: page + 1,
                limit: rowsPerPage,
                panel_type: 'customer',
            };
            if (statusFilter !== 'all') params.status_filter = statusFilter.toUpperCase();
            if (viewFilter === 'all_users' && selectedFilterUser) {
                params.target_user_id = selectedFilterUser.id || selectedFilterUser.user_id;
            }
            if (viewFilter === 'mine') params.view_filter = 'mine';

            const response = await axiosInstance.get('/reminders', { params });
            const data = response.data;
            setReminders(data.data || data.reminders || []);
            setTotalCount(data.total || data.totalCount || data.totalReminders || 0);
        } catch (err) {
            console.error('Failed to fetch reminders:', err);
            setError(err.response?.data?.message || 'Failed to load reminders.');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, viewFilter, selectedFilterUser, page, rowsPerPage]);

    useEffect(() => {
        document.title = 'Reminders | Customer Panel';
        fetchReminders();
    }, [fetchReminders]);

    const handleOpenDialog = () => {
        const now = new Date();
        now.setHours(now.getHours() + 1);
        const defaultDate = now.toISOString().slice(0, 16);
        setFormData({ ...INITIAL_FORM, remind_at: defaultDate });
        setCreateRecipient(null);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setFormData(INITIAL_FORM);
        setCreateRecipient(null);
    };

    const handleSubmit = async () => {
        if (!formData.title.trim()) {
            showSnack('Title is required.', 'warning');
            return;
        }
        if (!formData.remind_at) {
            showSnack('Reminder date is required.', 'warning');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                remind_at: new Date(formData.remind_at).toISOString(),
                panel_type: 'customer',
            };
            if (createRecipient) {
                payload.user_id = createRecipient.id || createRecipient.user_id;
            }
            await axiosInstance.post('/reminders', payload);
            showSnack('Reminder created successfully!');
            handleCloseDialog();
            fetchReminders();
        } catch (err) {
            console.error('Failed to create reminder:', err);
            showSnack(err.response?.data?.message || 'Failed to create reminder.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSnooze = async (reminder) => {
        const reminderId = reminder.id || reminder._id;
        try {
            await axiosInstance.put(`/reminders/${reminderId}/status`, {
                action: 'snooze',
                duration_minutes: 60,
            });
            showSnack('Reminder snoozed for 1 hour!');
            fetchReminders();
        } catch (err) {
            console.error('Failed to snooze:', err);
            showSnack(err.response?.data?.message || 'Failed to snooze reminder.', 'error');
        }
    };

    const handleDismiss = async (reminder) => {
        const reminderId = reminder.id || reminder._id;
        try {
            await axiosInstance.put(`/reminders/${reminderId}/status`, { action: 'dismiss' });
            showSnack('Reminder dismissed!');
            fetchReminders();
        } catch (err) {
            console.error('Failed to dismiss:', err);
            showSnack(err.response?.data?.message || 'Failed to dismiss reminder.', 'error');
        }
    };

    const handleDelete = async (reminder) => {
        const reminderId = reminder.id || reminder._id;
        if (!window.confirm(`Delete reminder "${reminder.title}"?`)) return;
        try {
            await axiosInstance.delete(`/reminders/${reminderId}`);
            showSnack('Reminder deleted!');
            fetchReminders();
        } catch (err) {
            console.error('Failed to delete reminder:', err);
            showSnack(err.response?.data?.message || 'Failed to delete reminder.', 'error');
        }
    };

    const handleResetFilters = () => {
        setViewFilter('mine');
        setStatusFilter('all');
        setSelectedFilterUser(null);
        setPage(0);
    };

    const getPriorityChip = (priority) => {
        const key = (priority || 'medium').toLowerCase();
        const config = PRIORITY_CONFIG[key] || PRIORITY_CONFIG.medium;
        return (
            <Chip label={config.label} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', bgcolor: config.bg, color: config.color }} />
        );
    };

    const getStatusChip = (status) => {
        const key = (status || 'active').toLowerCase();
        const config = STATUS_CONFIG[key] || STATUS_CONFIG.active;
        return (
            <Chip label={config.label} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', bgcolor: config.bg, color: config.color }} />
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2, px: 3, mb: 3, borderRadius: '16px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AlarmIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Reminder Management</Typography>
                            <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>Manage reminders with snooze, dismiss, and user filters</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchReminders} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
                        </Tooltip>
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={handleOpenDialog}
                            sx={{
                                fontFamily, fontWeight: 600, bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: '10px', textTransform: 'none', px: 3,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Create Reminder
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>{error}</Alert>
            )}

            {/* Filter Bar */}
            <Paper sx={{
                mb: 2, p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'grey.100',
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>Filters:</Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select
                        value={viewFilter}
                        onChange={(e) => { setViewFilter(e.target.value); setPage(0); }}
                        sx={{ fontFamily, borderRadius: '10px', fontSize: '0.85rem' }}
                    >
                        <MenuItem value="mine" sx={{ fontFamily }}>Reminders for Me</MenuItem>
                        <MenuItem value="all_users" sx={{ fontFamily }}>Reminders for Other Users</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{ width: 240, opacity: viewFilter !== 'all_users' ? 0.5 : 1, pointerEvents: viewFilter !== 'all_users' ? 'none' : 'auto' }}>
                    <UserSearchInput
                        selectedUser={selectedFilterUser}
                        onSelectUser={(u) => { setSelectedFilterUser(u); setPage(0); }}
                        onClearUser={() => { setSelectedFilterUser(null); setPage(0); }}
                        placeholder="Filter by user..."
                    />
                </Box>
                <Button onClick={handleResetFilters} sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT, textTransform: 'none', fontSize: '0.85rem' }}>
                    Reset Filters
                </Button>
            </Paper>

            <Paper
                sx={{
                    borderRadius: '16px', border: '1px solid', borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden',
                }}
            >
                {/* Status Tabs */}
                <Tabs
                    value={statusFilter}
                    onChange={(e, newVal) => { setStatusFilter(newVal); setPage(0); }}
                    sx={{
                        px: 2, borderBottom: '1px solid', borderColor: 'grey.100',
                        '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', minHeight: 48 },
                        '& .Mui-selected': { color: GREEN_ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: GREEN_ACCENT },
                    }}
                >
                    {STATUS_TABS.map((tab) => (
                        <Tab key={tab} label={tab.charAt(0).toUpperCase() + tab.slice(1)} value={tab} />
                    ))}
                </Tabs>

                {/* Search */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        placeholder="Search reminders..."
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{ flex: 1, maxWidth: 400, '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchReminders} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton>
                    </Tooltip>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['Title', 'Recipient', 'Creator', 'Remind At', 'Snoozed Until', 'Priority', 'Status', 'Actions'].map((header) => (
                                    <TableCell key={header} sx={{
                                        fontFamily, fontWeight: 700, color: '#64748b',
                                        fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                                    }}>
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : reminders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                        <AlarmIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No reminders found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                reminders.map((reminder) => {
                                    const isForSelf = reminder.user_id === currentUserId;
                                    const isDismissed = (reminder.status || '').toLowerCase() === 'dismissed';
                                    return (
                                        <TableRow
                                            key={reminder.id || reminder._id}
                                            sx={{ '&:hover': { bgcolor: '#f8fafc' }, opacity: isDismissed ? 0.6 : 1 }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                {reminder.title || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                {isForSelf ? 'Myself' : (reminder.user_name || '--')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                {reminder.created_by_id === currentUserId ? 'Myself' : (reminder.creator_name || '--')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                {formatDateTime(reminder.remind_at)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#8b5cf6', fontSize: '0.85rem' }}>
                                                {reminder.snoozed_until ? formatDateTime(reminder.snoozed_until) : '--'}
                                            </TableCell>
                                            <TableCell>{getPriorityChip(reminder.priority)}</TableCell>
                                            <TableCell>{getStatusChip(reminder.status)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Tooltip title="View">
                                                        <IconButton size="small" onClick={() => setViewReminder(reminder)} sx={{ color: '#3b82f6' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {isForSelf && !isDismissed && (
                                                        <>
                                                            <Tooltip title="Snooze 1 hour">
                                                                <IconButton size="small" onClick={() => handleSnooze(reminder)} sx={{ color: '#8b5cf6' }}>
                                                                    <SnoozeIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Dismiss">
                                                                <IconButton size="small" onClick={() => handleDismiss(reminder)} sx={{ color: '#ef4444' }}>
                                                                    <NotInterestedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDelete(reminder)} sx={{ color: '#ef4444' }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '& *': { fontFamily } }}
                />
            </Paper>

            {/* View Reminder Dialog */}
            <ViewReminderDialog
                reminder={viewReminder}
                open={!!viewReminder}
                onClose={() => setViewReminder(null)}
                currentUserId={currentUserId}
            />

            {/* Create Reminder Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Create New Reminder</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', mb: 1 }}>
                                Recipient
                            </Typography>
                            <UserSearchInput
                                selectedUser={createRecipient}
                                onSelectUser={setCreateRecipient}
                                onClearUser={() => setCreateRecipient(null)}
                                placeholder="Search by name or email..."
                            />
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                                Leave empty to create a reminder for yourself.
                            </Typography>
                        </Box>
                        <TextField
                            label="Title"
                            fullWidth
                            value={formData.title}
                            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Message (Optional)"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.message}
                            onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ fontFamily }}>Priority</InputLabel>
                                <Select
                                    value={formData.priority}
                                    label="Priority"
                                    onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    <MenuItem value="low" sx={{ fontFamily }}>Low</MenuItem>
                                    <MenuItem value="medium" sx={{ fontFamily }}>Medium</MenuItem>
                                    <MenuItem value="high" sx={{ fontFamily }}>High</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Remind At"
                                type="datetime-local"
                                fullWidth
                                value={formData.remind_at}
                                onChange={(e) => setFormData((prev) => ({ ...prev, remind_at: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseDialog} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                            textTransform: 'none', '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        {submitting ? 'Creating...' : 'Create Reminder'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
